import React, { useState, useEffect } from 'react';
import { useTheme } from './ThemeContext';
import axios from './config/axiosConfig';
import './MonthSelector.css';

const months = [
  'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
  'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'
];

function MonthSelector({ onSelectMonth, clienteInfo, onLogout, isAdmin = false }) {
  const currentDate = new Date();
  const currentYear = currentDate.getFullYear();

  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [totaisMensais, setTotaisMensais] = useState({});
  const [loading, setLoading] = useState(false);
  const [contasFiltro, setContasFiltro] = useState([]);
  const [contaSelecionada, setContaSelecionada] = useState('');
  const { theme, toggleTheme } = useTheme();

  // Função para formatar valores em moeda
  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  // Buscar contas correntes
  useEffect(() => {
    const fetchContas = async () => {
      try {
        let response;

        if (isAdmin) {
          const token = localStorage.getItem('token');
          response = await axios.post('/api/admin/omie/contas-correntes', {
            clienteId: clienteInfo?.id
          }, {
            headers: { Authorization: `Bearer ${token}` }
          });
        } else {
          response = await axios.post('/api/omie/contas-correntes');
        }

        setContasFiltro(response.data || []);
      } catch (error) {
        console.error('Erro ao buscar contas correntes:', error);
        setContasFiltro([]);
      }
    };

    if (clienteInfo) {
      fetchContas();
    }
  }, [isAdmin, clienteInfo]);

  // Buscar totais mensais quando o ano ou conta selecionada mudar
  useEffect(() => {
    const fetchTotaisMensais = async () => {
      setLoading(true);
      try {
        let response;

        // Preparar params sem undefined
        const baseParams = { ano: selectedYear };
        if (contaSelecionada) {
          baseParams.contaCorrente = contaSelecionada;
        }

        console.log('[MonthSelector] Buscando totais com filtro:', baseParams);

        if (isAdmin) {
          // Admin mode: call admin endpoint with token and clientId
          const token = localStorage.getItem('token');
          response = await axios.get('/api/admin/lancamentos/totais-ano', {
            params: {
              ...baseParams,
              clienteId: clienteInfo?.id
            },
            headers: {
              Authorization: `Bearer ${token}`
            }
          });
        } else {
          // Client mode: call client endpoint (token is handled by axios interceptor)
          response = await axios.get('/api/lancamentos/totais-ano', {
            params: baseParams
          });
        }

        // Converter array em objeto para acesso rápido por mês
        const totaisObj = {};
        response.data.forEach(item => {
          totaisObj[item.mes] = {
            total: item.total,
            count: item.count
          };
        });

        setTotaisMensais(totaisObj);
      } catch (error) {
        console.error('Erro ao buscar totais mensais:', error);
        setTotaisMensais({});
      } finally {
        setLoading(false);
      }
    };

    fetchTotaisMensais();
  }, [selectedYear, contaSelecionada, isAdmin, clienteInfo]);

  return (
    <div className="month-selector-container">
      <div className="month-selector-card">
        <div className="selector-header">
          <div className="client-info">
            <h3>Bem-vindo(a), {clienteInfo?.nome || 'Usuário'}</h3>
            <span className="client-email">{clienteInfo?.email}</span>
          </div>
          <div className="header-actions">
            <button onClick={toggleTheme} className="theme-toggle-btn">
              {theme === 'light' ? '🌙 Modo Escuro' : '☀️ Modo Claro'}
            </button>
            <button onClick={onLogout} className="logout-btn">
              Sair
            </button>
          </div>
        </div>

        <div className="year-selector">
          <button
            className="nav-button"
            onClick={() => setSelectedYear(selectedYear - 1)}
          >
            &lt;
          </button>
          <h2>{selectedYear}</h2>
          {/*
            *** ALTERAÇÃO AQUI ***
            A condição "{selectedYear < currentYear && (...)}" foi removida,
            permitindo que o botão de avançar (>) apareça sempre.
          */}
          <button
            className="nav-button"
            onClick={() => setSelectedYear(selectedYear + 1)}
          >
            &gt;
          </button>
        </div>

        <div className="conta-filter">
          <label htmlFor="conta-select">Filtrar por Conta Corrente:</label>
          <select
            id="conta-select"
            value={contaSelecionada}
            onChange={(e) => setContaSelecionada(e.target.value)}
            className="conta-select"
          >
            <option value="">Todas as contas</option>
            {contasFiltro.map(conta => (
              <option key={conta.codigo} value={conta.descricao}>
                {conta.descricao}
              </option>
            ))}
          </select>
        </div>

        {loading && (
          <div style={{ textAlign: 'center', padding: '1rem', color: 'var(--text-secondary)' }}>
            Carregando valores...
          </div>
        )}

        <div className="month-grid">
          {months.map((month, index) => {
            const mesNumero = index + 1;
            const totalInfo = totaisMensais[mesNumero];
            const hasData = totalInfo && totalInfo.total > 0;

            return (
              <button
                key={index}
                className={`month-button ${hasData ? 'has-data' : ''}`}
                onClick={() => onSelectMonth(mesNumero, selectedYear)}
                disabled={loading}
              >
                <span className="month-name">{month}</span>
                {loading ? (
                  <span className="month-total loading">Carregando...</span>
                ) : hasData ? (
                  <span className="month-total">{formatCurrency(totalInfo.total)}</span>
                ) : (
                  <span className="month-total empty">Sem lançamentos</span>
                )}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export default MonthSelector;